import React, { useState, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';

// Popup to import expenses from a CSV / Excel file (same columns as the Template)
// Columns: Date, Category, PaymentMethod, Amount, VehiclePlate, VendorName, Notes
const norm = (s) => String(s || '').trim().toLowerCase().replace(/[\s_]/g, '');

const toISODate = (v) => {
    if (v === undefined || v === null || v === '') return '';
    if (typeof v === 'number') { // Excel serial date
        const d = XLSX.SSF.parse_date_code(v);
        if (d) return `${d.y}-${String(d.m).padStart(2, '0')}-${String(d.d).padStart(2, '0')}`;
    }
    const s = String(v).trim();
    let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
    m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/); // DD/MM/YYYY
    if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
    const d = new Date(s);
    return isNaN(d) ? '' : d.toISOString().slice(0, 10);
};

export default function ExpenseImportModal({ open, onClose, onImported, operatorId, vehicles = [], vendors = [], onDownloadTemplate }) {
    const [file, setFile] = useState(null);
    const [rows, setRows] = useState([]);
    const [error, setError] = useState('');
    const [dragOver, setDragOver] = useState(false);
    const [importing, setImporting] = useState(false);
    const [progress, setProgress] = useState(0);
    const [result, setResult] = useState(null);
    const inputRef = useRef(null);

    if (!open) return null;

    const reset = () => {
        setFile(null); setRows([]); setError(''); setProgress(0); setResult(null);
        if (inputRef.current) inputRef.current.value = '';
    };
    const close = () => { if (importing) return; reset(); onClose(); };

    const parseFile = (f) => {
        reset();
        if (!f) return;
        if (!/\.(csv|xlsx|xls)$/i.test(f.name)) { setError('Please choose a .csv, .xlsx or .xls file.'); return; }
        setFile(f);
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const wb = XLSX.read(e.target.result, { type: 'array' });
                const sheet = wb.Sheets[wb.SheetNames[0]];
                const raw = XLSX.utils.sheet_to_json(sheet, { defval: '' });
                if (!raw.length) { setError('The file has no data rows.'); return; }
                const parsed = raw.map((r, i) => {
                    const get = (...keys) => {
                        const k = Object.keys(r).find(key => keys.includes(norm(key)));
                        return k !== undefined ? r[k] : '';
                    };
                    const plate = String(get('vehicleplate', 'plate', 'plateno.', 'plateno')).trim();
                    const vendorName = String(get('vendorname', 'vendor')).trim();
                    const vehicle = plate ? vehicles.find(v => norm(v.plateNumber) === norm(plate)) : null;
                    const vendor = vendorName ? vendors.find(v => norm(v.vendorName) === norm(vendorName)) : null;
                    const row = {
                        line: i + 2,
                        date: toISODate(get('date')),
                        category: String(get('category')).trim(),
                        paymentMethod: String(get('paymentmethod', 'payment')).trim(),
                        amount: parseFloat(String(get('amount', 'total')).replace(/[^\d.-]/g, '')),
                        plate, vendorName,
                        vehicleId: vehicle?._id || '',
                        vendorId: vendor?._id || '',
                        notes: String(get('notes', 'description')).trim(),
                    };
                    const problems = [];
                    if (!row.date) problems.push('invalid date');
                    if (!row.category) problems.push('missing category');
                    if (!row.paymentMethod) problems.push('missing payment method');
                    if (!(row.amount > 0)) problems.push('invalid amount');
                    const warnings = [];
                    if (plate && !vehicle) warnings.push(`plate "${plate}" not found`);
                    if (vendorName && !vendor) warnings.push(`vendor "${vendorName}" not found`);
                    return { ...row, problems, warnings };
                });
                setRows(parsed);
            } catch (err) {
                console.error(err);
                setError('Could not read this file. Please use the Template format.');
            }
        };
        reader.readAsArrayBuffer(f);
    };

    const validRows = rows.filter(r => r.problems.length === 0);

    const handleImport = async () => {
        if (!validRows.length) return;
        setImporting(true); setProgress(0);
        let ok = 0; const failed = [];
        for (let i = 0; i < validRows.length; i++) {
            const r = validRows[i];
            const data = new FormData();
            data.append('operatorId', operatorId || 'admin');
            data.append('date', r.date);
            data.append('category', r.category);
            data.append('paymentMethod', r.paymentMethod);
            data.append('amount', r.amount);
            const extraNotes = [r.notes, r.plate && !r.vehicleId ? `Plate: ${r.plate}` : '', r.vendorName && !r.vendorId ? `Vendor: ${r.vendorName}` : ''].filter(Boolean).join(' | ');
            data.append('notes', extraNotes);
            data.append('vehicleId', r.vehicleId);
            data.append('vendorId', r.vendorId);
            try { await axios.post('/api/expenses', data); ok++; }
            catch (err) { failed.push({ line: r.line, msg: err.response?.data?.message || err.message }); }
            setProgress(Math.round(((i + 1) / validRows.length) * 100));
        }
        setImporting(false);
        setResult({ ok, failed, skipped: rows.length - validRows.length });
        if (ok > 0) onImported?.();
    };

    const fmtSize = (b) => b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;

    return (
        <div onClick={close} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 760, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
                {/* Header */}
                <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>Import Expenses</h3>
                        <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>Upload a CSV or Excel file using the expense template format.</p>
                    </div>
                    <button onClick={close} style={{ border: 'none', background: '#f1f5f9', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', fontSize: 18, color: '#64748b' }}>×</button>
                </div>

                {/* Body */}
                <div style={{ padding: 24, overflowY: 'auto' }}>
                    {result ? (
                        <div style={{ textAlign: 'center', padding: '20px 0' }}>
                            <div style={{ fontSize: 40, marginBottom: 8, color: result.ok ? '#10b981' : '#ef4444' }}>{result.ok ? '✓' : '!'}</div>
                            <h4 style={{ margin: '0 0 6px', fontSize: 17, color: '#0f172a' }}>{result.ok} expense{result.ok === 1 ? '' : 's'} imported</h4>
                            <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>
                                {result.skipped > 0 && `${result.skipped} row(s) skipped due to errors. `}
                                {result.failed.length > 0 && `${result.failed.length} row(s) failed to save.`}
                            </p>
                            {result.failed.length > 0 && (
                                <div style={{ marginTop: 14, textAlign: 'left', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: 12, fontSize: 12, color: '#991b1b' }}>
                                    {result.failed.map(f => <div key={f.line}>Row {f.line}: {f.msg}</div>)}
                                </div>
                            )}
                        </div>
                    ) : (
                        <>
                            {/* Upload field */}
                            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>Upload File *</label>
                            <div
                                onClick={() => inputRef.current?.click()}
                                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                                onDragLeave={() => setDragOver(false)}
                                onDrop={e => { e.preventDefault(); setDragOver(false); parseFile(e.dataTransfer.files[0]); }}
                                style={{ border: `2px dashed ${dragOver ? '#2563eb' : '#cbd5e1'}`, background: dragOver ? '#eff6ff' : '#f8fafc', borderRadius: 12, padding: '28px 16px', textAlign: 'center', cursor: 'pointer', transition: 'all .15s' }}
                            >
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M17 8l-5-5-5 5" /><path d="M12 3v12" /></svg>
                                <div style={{ marginTop: 8, fontSize: 14, fontWeight: 700, color: '#1e293b' }}>
                                    {file ? file.name : 'Click to choose a file or drag it here'}
                                </div>
                                <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                                    {file ? fmtSize(file.size) : 'Supported: .csv, .xlsx, .xls'}
                                </div>
                                <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} onChange={e => parseFile(e.target.files[0])} />
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, fontSize: 12, color: '#64748b' }}>
                                <span>Columns: Date, Category, PaymentMethod, Amount, VehiclePlate, VendorName, Notes</span>
                                {onDownloadTemplate && (
                                    <button type="button" onClick={onDownloadTemplate} style={{ border: 'none', background: 'none', color: '#2563eb', fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>Download template</button>
                                )}
                            </div>

                            {error && <div style={{ marginTop: 14, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, color: '#b91c1c', fontSize: 13 }}>{error}</div>}

                            {/* Preview */}
                            {rows.length > 0 && (
                                <div style={{ marginTop: 18 }}>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>
                                        Preview — {validRows.length} of {rows.length} row(s) ready to import
                                    </div>
                                    <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'auto', maxHeight: 260 }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                            <thead style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
                                                <tr>{['Row', 'Date', 'Category', 'Payment', 'Amount', 'Plate', 'Vendor', 'Status'].map(h => (
                                                    <th key={h} style={{ textAlign: 'left', padding: '8px 10px', color: '#64748b', fontWeight: 700, borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                                                ))}</tr>
                                            </thead>
                                            <tbody>
                                                {rows.map(r => (
                                                    <tr key={r.line} style={{ background: r.problems.length ? '#fef2f2' : 'white' }}>
                                                        <td style={td}>{r.line}</td>
                                                        <td style={td}>{r.date || '—'}</td>
                                                        <td style={td}>{r.category || '—'}</td>
                                                        <td style={td}>{r.paymentMethod || '—'}</td>
                                                        <td style={td}>{isNaN(r.amount) ? '—' : r.amount.toFixed(2)}</td>
                                                        <td style={td}>{r.plate || '—'}</td>
                                                        <td style={td}>{r.vendorName || '—'}</td>
                                                        <td style={{ ...td, fontWeight: 600, color: r.problems.length ? '#dc2626' : r.warnings.length ? '#d97706' : '#16a34a' }}>
                                                            {r.problems.length ? `Skip: ${r.problems.join(', ')}` : r.warnings.length ? `OK (${r.warnings.join(', ')})` : 'Ready'}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {importing && (
                                <div style={{ marginTop: 16 }}>
                                    <div style={{ height: 8, background: '#e2e8f0', borderRadius: 99, overflow: 'hidden' }}>
                                        <div style={{ width: `${progress}%`, height: '100%', background: '#2563eb', transition: 'width .2s' }} />
                                    </div>
                                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>Importing… {progress}%</div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                    {result ? (
                        <>
                            <button onClick={reset} style={btnOutline}>Import Another File</button>
                            <button onClick={close} style={btnPrimary}>Done</button>
                        </>
                    ) : (
                        <>
                            <button onClick={close} disabled={importing} style={btnOutline}>Cancel</button>
                            <button onClick={handleImport} disabled={!validRows.length || importing}
                                style={{ ...btnPrimary, opacity: !validRows.length || importing ? 0.5 : 1, cursor: !validRows.length || importing ? 'not-allowed' : 'pointer' }}>
                                {importing ? 'Importing…' : `Upload & Import${validRows.length ? ` (${validRows.length})` : ''}`}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

const td = { padding: '7px 10px', borderBottom: '1px solid #f1f5f9', color: '#334155', whiteSpace: 'nowrap' };
const btnOutline = { padding: '10px 18px', borderRadius: 10, border: '1px solid #e2e8f0', background: 'white', color: '#475569', fontWeight: 700, fontSize: 14, cursor: 'pointer' };
const btnPrimary = { padding: '10px 18px', borderRadius: 10, border: 'none', background: '#1e40af', color: 'white', fontWeight: 700, fontSize: 14, cursor: 'pointer' };
